import React, { useState, useEffect } from "react";
import { doc, getDoc, updateDoc, arrayUnion } from "firebase/firestore";
import moment from "moment";
import { db } from "../../lib/firebase";
import "../../styles/Transfer.css";
import "../../styles/Deposit.css";
import { useNavigate } from "react-router-dom";
import { sendTransactionEmail } from "../../lib/Email";
import Verification from "./Verification";

const Withdraw = ({ user, setInvestedBalance }) => {
  const [beneficiary, setBeneficiary] = useState("");
  const [beneficiaryAccount, setBeneficiaryAccount] = useState("");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [date, setDate] = useState(moment().format("YYYY-MM-DD"));
  const [account, setAccount] = useState("Investments");
  const [accountBalances, setAccountBalances] = useState({});
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [userGroup, setUserGroup] = useState(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [transactionDetails, setTransactionDetails] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    if (user && user.uid) {
      const fetchUserData = async () => {
        try {
          const userDoc = await getDoc(doc(db, "users", user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const fetchedTransactions = userData.transactions || [];
            const accountBalances = {};

            const userGroup = userData.group || "No group available";

            const credits = fetchedTransactions.reduce(
              (total, txn) =>
                txn.type === "credit" && txn.account === "Investments"
                  ? total + txn.amount
                  : total,
              0
            );
            const debits = fetchedTransactions.reduce(
              (total, txn) =>
                txn.type === "debit" && txn.account === "Investments"
                  ? total + txn.amount
                  : total,
              0
            );
            const totalBalance = credits - debits;

            accountBalances["Investments"] = totalBalance;

            setAccountBalances(accountBalances);
            setUserGroup(userGroup);
          }
        } catch (error) {
          console.error("Error fetching user data: ", error);
        }
      };

      fetchUserData();
    }
  }, [user, setInvestedBalance]);

  useEffect(() => {
    const parsedAmount = parseFloat(amount);
    const isValid =
      beneficiary &&
      beneficiaryAccount &&
      parsedAmount > 0 &&
      parsedAmount <= (accountBalances[account] || 0) &&
      description;
    setIsFormValid(isValid);
  }, [
    beneficiary,
    beneficiaryAccount,
    amount,
    description,
    accountBalances,
    account,
  ]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);

    if (!isFormValid) {
      setFormSubmitting(false);
      return;
    }

    const parsedAmount = parseFloat(amount);

    try {
      const transactionDescription = `${beneficiary} withdrawal`;
      const transaction = {
        account: "Investments",
        amount: parsedAmount,
        beneficiary: beneficiary,
        beneficiaryAccount: beneficiaryAccount,
        date: moment(date).format("MMMM D, YYYY"),
        description:
          userGroup === "deactivated"
            ? `[Pending] ${beneficiary} withdrawal`
            : transactionDescription,
        type: "debit",
        explore: null,
        status: "pending",
        sender: "Sky FXT",
      };

      await updateDoc(doc(db, "users", user.uid), {
        transactions: arrayUnion(transaction),
      });

      const userDocRef = doc(db, "users", user.uid);
      const userDoc = await getDoc(userDocRef);
      const userData = userDoc.data();
      const fullName = userData.fullName;
      const accountNumber = userData.accountNumber;

      // ✅ Send confirmation email
      await sendTransactionEmail(
        user.email,
        accountNumber,
        fullName,
        transaction
      );

      // ✅ If user's group is 'deactivated', update to 'cancelled'
      if (userGroup === "deactivated") {
        await updateDoc(doc(db, "users", user.uid), { group: "cancelled" });
        setUserGroup("cancelled"); // Update local state
      }

      setTransactionDetails(transaction);
      setIsSuccess(true);
      setBeneficiary("");
      setBeneficiaryAccount("");
      setAmount("");
      setDescription("");
      setDate(moment().format("YYYY-MM-DD"));
      setAccount("Investments");
    } catch (error) {
      console.error("Error adding transaction: ", error);
    } finally {
      setFormSubmitting(false);
    }
  };

  // If group is natively cancelled, block them from the form
  if (userGroup === "cancelled" && !isSuccess) {
    return (
      <div className="deposit-page">
        <div style={{ textAlign: "left", padding: "40px 20px" }}>
          <h2 style={{ color: "#D52A2A", marginBottom: "16px" }}>Withdrawal Unavailable</h2>
          <p style={{ color: "#aaa", fontSize: "16px", marginBottom: "24px" }}>
            You currently cannot withdraw at the moment. Please contact support or complete any required verification.
          </p>
        </div>
      </div>
    );
  }

  // If transaction went through
  if (isSuccess) {
    return (
      <div className="deposit-page" style={{ position: "relative" }}>
        <div className="pending-confirmation">
          <h2>Withdrawal Pending</h2>
          <p className="confirm-text">
            Your withdrawal request has been submitted and is pending
            confirmation.
          </p>
          <div className="pending-details">
            <div className="pending-row">
              <span>Transaction Type</span>
              <span>Withdrawal</span>
            </div>
            <div className="pending-row">
              <span>Crypto</span>
              <span>{transactionDetails?.beneficiary}</span>
            </div>
            <div className="pending-row">
              <span>Wallet Address</span>
              <span
                style={{
                  wordBreak: "break-all",
                  fontFamily: "monospace",
                  fontSize: "13px",
                }}
              >
                {transactionDetails?.beneficiaryAccount}
              </span>
            </div>
            <div className="pending-row">
              <span>Amount</span>
              <span>${transactionDetails?.amount?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
            </div>
            <div className="pending-row">
              <span>Status</span>
              <span className="pending-status">Pending</span>
            </div>
            <div className="pending-row">
              <span>Date</span>
              <span>{transactionDetails?.date}</span>
            </div>
          </div>
          <button
            onClick={() => navigate("/fxt")}
            className="delete-button"
            style={{ marginTop: "20px" }}
          >
            Back to Investments
          </button>
        </div>

        {/* Verification overlay if group was deactivated and is now cancelled */}
        {userGroup === "cancelled" && (
          <Verification setShowVerificationBox={() => navigate("/fxt")} />
        )}
      </div>
    );
  }

  return (
    <div className="deposit-page">
      <h2>Withdraw Crypto</h2>
      <p className="box-text">
        Enter your withdrawal details below to transfer funds from your
        Investments balance.
      </p>
      <form onSubmit={handleSubmit}>
        <div className="form-cover">
          <div>
            <label className="transaction-head">Select Crypto:</label>
            <select
              value={beneficiary}
              className="input-field"
              onChange={(e) => setBeneficiary(e.target.value)}
              required
            >
              <option value="" disabled>
                Choose an option
              </option>
              <option value="Bitcoin">BTC</option>
              <option value="Ethereum">ETH</option>
              <option value="USDT">USDT (TRC20)</option>
            </select>
          </div>

          <div>
            <label className="transaction-head">Wallet Address:</label>
            <input
              type="text"
              value={beneficiaryAccount}
              className="input-field"
              placeholder="e.g., TAyXZ83DoETATs4q99iwUYfC1huRNcodtp"
              onChange={(e) => setBeneficiaryAccount(e.target.value)}
              required
            />
          </div>
        </div>

        <div className="form-cover">
          <div>
            <label className="transaction-head">Amount:</label>
            <input
              type="number"
              value={amount}
              className="input-field"
              placeholder="e.g., 100"
              onChange={(e) => setAmount(e.target.value)}
              required
              min="0.01"
              step="any"
            />
            <div style={{ color: "#888", fontSize: "13px", marginTop: "6px" }}>
              Available to Withdraw: $
              {accountBalances["Investments"] !== undefined
                ? accountBalances["Investments"].toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })
                : "0.00"}
            </div>
          </div>

          <div>
            <label className="transaction-head">Description:</label>
            <input
              type="text"
              className="input-field"
              value={description}
              placeholder="e.g., First Withdrawal"
              onChange={(e) => setDescription(e.target.value)}
              required
            />
          </div>
        </div>

        <div style={{ marginTop: "20px" }}>

          <button
            type="submit"
            className="delete-button"
            disabled={formSubmitting || !isFormValid}
          >
            {formSubmitting ? "Processing..." : "Withdraw"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Withdraw;
